'use client'

import { LineArrowLeft } from '@qr-front/icons/line-arrow-left'
import { LineEmailFast } from '@qr-front/icons/line-email-fast'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { ExistingAccountNotice } from './components/ExistingAccountNotice'
import { OtpCodeForm } from './components/OtpCodeForm'

type TSignUpExistingAccountStepProps = {
	email: string
	resendCooldownSeconds: number
	containerId: string
	onBack: () => void
	onVerifyOtp: (code: string) => Promise<void>
	onResendOtp: () => Promise<number>
}

export const SignUpExistingAccountStep = ({
	email,
	resendCooldownSeconds,
	containerId,
	onBack,
	onVerifyOtp,
	onResendOtp,
}: TSignUpExistingAccountStepProps) => {
	const t = useTranslations()
	const [isPending, setIsPending] = useState(false)

	const whilePending =
		<TArgs extends unknown[], TResult>(
			request: (...args: TArgs) => Promise<TResult>,
		) =>
		async (...args: TArgs): Promise<TResult> => {
			setIsPending(true)
			try {
				return await request(...args)
			} finally {
				setIsPending(false)
			}
		}

	return (
		<div className="flex w-full flex-1 flex-col gap-8 text-start">
			<button
				type="button"
				className="type-body-medium flex items-center gap-2 self-start text-dark-grey disabled:cursor-not-allowed disabled:opacity-50"
				onClick={onBack}
				disabled={isPending}
			>
				<LineArrowLeft className="size-4" />
				{t('auth__common__back')}
			</button>
			<div className="flex w-full flex-1 flex-col items-center justify-center gap-6 text-center">
				<LineEmailFast className="size-12 text-blue" />
				<div className="flex w-full flex-col gap-2">
					<div className="type-heading-3 text-black">
						{t('auth__login_otp__title')}
					</div>
					<div className="type-body-medium text-dark-grey">
						{t.rich('auth__login_otp__description', {
							email,
							em: () => (
								<span className="block max-w-full truncate text-blue tablet:inline">
									{email}
								</span>
							),
						})}
					</div>
				</div>
				<ExistingAccountNotice
					message={t('auth__signup_existing_account__otp_info')}
				/>
				<OtpCodeForm
					layout="inline"
					resendCooldownSeconds={resendCooldownSeconds}
					submitLabel={t('ui__generic__continue')}
					containerId={containerId}
					onVerify={whilePending(onVerifyOtp)}
					onResend={whilePending(onResendOtp)}
				/>
			</div>
		</div>
	)
}
